import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isAdminAppPath } from '../context/AuthContext';

const EDITABLE_SELECTOR =
  'input, textarea, select, [contenteditable="true"], [contenteditable=""]';

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest(EDITABLE_SELECTOR));
};

/**
 * Dissuasion anti-copie / anti-enregistrement navigateur.
 * Ne peut pas bloquer une capture OS (Impr. écran, enregistreur système).
 */
const ContentProtection: React.FC = () => {
  const { pathname } = useLocation();
  const disabled =
    pathname.startsWith('/print/') ||
    pathname.startsWith('/print-lettre/') ||
    isAdminAppPath(pathname);

  useEffect(() => {
    if (disabled) {
      document.documentElement.classList.remove('content-protected');
      return;
    }

    document.documentElement.classList.add('content-protected');

    const onContextMenu = (e: MouseEvent) => {
      if (isEditableTarget(e.target)) return;
      e.preventDefault();
    };

    const onDragStart = (e: DragEvent) => {
      if (isEditableTarget(e.target)) return;
      if (e.target instanceof HTMLImageElement || e.target instanceof HTMLAnchorElement) {
        e.preventDefault();
      }
    };

    const onCopyOrCut = (e: ClipboardEvent) => {
      if (isEditableTarget(e.target)) return;
      e.preventDefault();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (isEditableTarget(e.target)) return;

      const key = e.key.toLowerCase();
      const mod = e.ctrlKey || e.metaKey;

      // Enregistrer la page, imprimer via navigateur, voir le code source
      if (mod && (key === 's' || key === 'p' || key === 'u')) {
        e.preventDefault();
        return;
      }

      // Raccourcis DevTools courants
      if (
        key === 'f12' ||
        (mod && e.shiftKey && (key === 'i' || key === 'j' || key === 'c')) ||
        (mod && e.altKey && key === 'i')
      ) {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', onContextMenu);
    document.addEventListener('dragstart', onDragStart);
    document.addEventListener('copy', onCopyOrCut);
    document.addEventListener('cut', onCopyOrCut);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.documentElement.classList.remove('content-protected');
      document.removeEventListener('contextmenu', onContextMenu);
      document.removeEventListener('dragstart', onDragStart);
      document.removeEventListener('copy', onCopyOrCut);
      document.removeEventListener('cut', onCopyOrCut);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [disabled]);

  return null;
};

export default ContentProtection;
